import { Component, OnInit, Inject, Input, OnDestroy } from '@angular/core';
import { Validators, FormBuilder } from '@angular/forms';

import {
    creditCardNumberValidator,
    creditCardMonthValidator,
    creditCardCVVCIDValidator,
    creditCardExpDateValidator,
    creditCardYearValidator
} from '@shared/core/validators/credit-cards.shared.validator';
import * as Utils from '@shared/core/utils';
import * as Services from '@shared/core/services';
import * as Tokens from '@shared/core/tokens';
import { Controllers } from '@shared/core/controllers';

import { FormTypesComponent } from '../form-types.shared.component';

import { Observable } from 'rxjs';

@Component({
    selector: 'shared-form-for-payment-method-redirect',
    templateUrl: './form-for-payment-method-redirect.shared.component.html',
    styleUrls: ['./form-for-payment-method-redirect.shared.component.scss']
})
export class FormForPaymentMethodRedirectComponent extends FormTypesComponent implements OnInit, OnDestroy {
    @Input() public allowCancel: boolean = false;

    public isMakingRequests$: Observable<boolean>;
    public canCancelForm$: Observable<boolean>;

    constructor(
        @Inject(Tokens.CONFIG_TOKEN) public config: IConfig,
        public formBuilder: FormBuilder,
        public controllers: Controllers,
        public creditCardsService: Services.CreditCardsService,
    ) {
        super(config, formBuilder);
        this.formName = 'payment-method-redirect';

        this.compact = false;
        this.labelSubmit = 'Add payment method';
    }

    ngOnInit(): void {
        super.ngOnInit();


        this.formControl = this.formBuilder.group({
            CardHolderName: [
                { value: '', disabled: false },
                {
                    validators: Validators.compose([
                        Validators.required,
                    ]),
                },
            ],
            CardNumber: [
                { value: '', disabled: false },
                {
                    validators: Validators.compose([
                        Validators.required,
                        Validators.minLength(15),
                        Validators.maxLength(19),
                        creditCardNumberValidator(true),
                    ]),
                },
            ],
            ExpiryMonth: [
                { value: '', disabled: false },
                {
                    validators: Validators.compose([
                        Validators.required,
                        Validators.minLength(2),
                        Validators.maxLength(2),
                        creditCardMonthValidator,
                    ]),
                    asyncValidators: Validators.composeAsync([
                        creditCardExpDateValidator('ExpiryMonth', 'ExpiryYear'),
                    ]),
                },
            ],
            ExpiryYear: [
                { value: '', disabled: false },
                {
                    validators: Validators.compose([
                        Validators.required,
                        Validators.minLength(2),
                        Validators.maxLength(2),
                        creditCardYearValidator,
                    ]),
                    asyncValidators: Validators.composeAsync([
                        creditCardExpDateValidator('ExpiryMonth', 'ExpiryYear'),
                    ]),
                },
            ],
            Cvc2: [
                { value: '', disabled: false },
                {
                    validators: Validators.compose([
                        Validators.required,
                    ]),
                    asyncValidators: Validators.composeAsync([
                        creditCardCVVCIDValidator('CardNumber'),
                    ]),

                },
            ],
            saveCard: [
                { value: this.guest === false, disabled: false }
            ],
            isDefaultPaymentMethod: [
                { value: this.guest === false, disabled: false }
            ],
        });

        this.isMakingRequests$ = this.controllers.creditCards.isMakingRequests$();
        this.canCancelForm$ = this.controllers.creditCards.canCancelAddingNewCardsForm$();

        if (!this.form || !this.form.nativeElement) {
            console.error('Unable to find form ElementRef for paymentMethodRedirect form. Add local reference in html #form to form element in order to use redirect');
        }
    }

    ngOnDestroy(): void {
        this.controllers.creditCards.resetCreditCardErrors();
    }

    private _submit(): void {
        this.creditCardsService.addCreditCardWithRedirect(
            Utils.CreditCards.remapRedirectCardDetails(
                Utils.Forms.extractValues(this.formControl) as OLO.CreditCards.ICreditCardRedirectDetails
            )
        ).then(({ redirectUrl, verificationToken, returnUrlAfterRedirect }) => {
            if (this.config.demoMode || !this.form?.nativeElement) {

                return;
            }

            if (this.config.paymentProvider === OLO.Enums.PAYMENT_PROVIDER.FAT_ZEBRA) {
                const v = Utils.Forms.extractValues(this.formControl) as OLO.CreditCards.ICreditCardRedirectDetails;
                const customForm: HTMLFormElement = document.createElement('form');
                customForm.method = 'GET';
                customForm.action = redirectUrl.replace('.json', '');

                const field = (name: string, value: any, isHidden: boolean = false): HTMLInputElement => {
                    const _: HTMLInputElement = document.createElement('input');
                    _.type = 'text';
                    _.name = name;
                    _.value = value;
                    if (isHidden) {
                        _.type = 'hidden';
                    }
                    customForm.appendChild(_);

                    return _;
                };

                const nameField = field('card_holder', v.CardHolderName);
                const ccField = field('card_number', v.CardNumber);
                const expMonthField = field('expiry_month', +v.ExpiryMonth);
                const expYearhField = field('expiry_year', '20' + v.ExpiryYear);
                const cvvField = field('cvv', v.Cvc2);
                const verification = field('verification', verificationToken, true);

                const rtnPath = returnUrlAfterRedirect || (window.location.origin + '/payment-gateway');
                const retPathField = field('return_path', rtnPath, true);

                document.body.appendChild(customForm);

                return customForm.submit();
            }

            /* Payment express */
            this.form.nativeElement.action = redirectUrl;
            this.form.nativeElement.submit();
            this.form.nativeElement.action = null;

        })
            .catch(ex => {
                console.error('There was an error while generating card token:', ex);
            });
    }

    public handleFormSubmit($event: Event): void {
        $event.preventDefault();

        this.onFormSubmit.emit({
            event: $event,
            form: this.formControl,
        });

        if (this.delay !== null) {
            setTimeout(this._submit.bind(this), this.delay);

            return;
        }

        this._submit();
    }

    public handleFormCancel($event: Event): void {
        this.controllers.creditCards.toggleCreditCardFormVisibilityWithCleanUp(false);
    }
}
